import { Stack } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CsrPillar } from "src/api/client-api/csrStrategy/useGetAllStrategy";
import { SortableRows } from "src/components/Dragging/SortableRows/SortableRows";
import { PillarCard } from "./PillarCard";

type ListPillarsProps = {
    pillars: CsrPillar[];
    hideHeader?: boolean;
    onEditPillar?: (pillar: CsrPillar) => void;
    onReorderPillars?: (pillar: CsrPillar) => Promise<void>;
    isReordering?: boolean;
};

export const ListPillars = (props: ListPillarsProps) => {
    const { pillars, onEditPillar, onReorderPillars, isReordering = false } = props;
    const { t } = useTranslation();
    const [optimisticPillars, setOptimisticPillars] = useState<CsrPillar[] | null>(null);
    const orderedPillars = optimisticPillars ?? pillars;
    const [collapsedPillarIds, setCollapsedPillarIds] = useState<Set<string>>(() => new Set());

    const togglePillar = (pillarId: string) => {
        setCollapsedPillarIds((current) => {
            const next = new Set(current);
            if (next.has(pillarId)) next.delete(pillarId);
            else next.add(pillarId);
            return next;
        });
    };

    const reorderPillars = async (nextPillars: CsrPillar[], movedPillar: CsrPillar) => {
        if (!onReorderPillars) return;

        const positionedPillars = nextPillars.map((pillar, position) => ({ ...pillar, position }));
        setOptimisticPillars(positionedPillars);
        try {
            await onReorderPillars(positionedPillars.find(({ id }) => id === movedPillar.id)!);
        } catch {
            // The parent handles error presentation; clearing the override below restores the server order.
        } finally {
            setOptimisticPillars(null);
        }
    };

    return (
        <Stack gap={2}>
            <SortableRows
                items={orderedPillars}
                disabled={!onReorderPillars || isReordering}
                getHandleLabel={(pillar) =>
                    t("settings.csr.overview.reorder_pillar", {
                        defaultValue: "Reorder {{name}}",
                        name: pillar.name,
                    })
                }
                onDragEnd={reorderPillars}
                renderItem={(pillar, dragHandle) => (
                    <PillarCard
                        pillar={pillar}
                        dragHandle={dragHandle}
                        expanded={!collapsedPillarIds.has(pillar.id)}
                        onToggle={() => togglePillar(pillar.id)}
                        onEdit={onEditPillar ? () => onEditPillar(pillar) : undefined}
                    />
                )}
            />
        </Stack>
    );
};
